import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { isInvoiceBillingType } from '~constants/maps';

import { ContainerHeader, ErrorResponse } from '~components';
import { faCopy } from '~components/icon';
import { Form as WizardForm } from '~components/wizard-form';

import { useToast } from '~libs/toast';

import { Budget, COPY_STATUS } from '~support-at-home/models';

import copyBudgetFields from '~support-at-home/forms/budgets/copyBudget';

import { getAll as getClients } from '~main/actions/clients';
import { useGetBudgetQuery, useSaveBudgetMutation } from '~support-at-home/actions/budgets';
import { getAll as getClientBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';
import { getAll as getClientContributions } from '~support-at-home/actions/clientContributions';
import { getAll as getClientServices } from '~support-at-home/actions/clientServices';
import { getAll as getPricePlans } from '~support-at-home/actions/pricePlans';
import { getAll as getSahBudgets } from '~support-at-home/actions/sahBudgets';

import { toggleQuickMenu } from '~main/reducers/app';

import { getErrorMessage } from '~libs/axiosWrapper';

const BudgetCopy: React.FC = () => {
  const { id: sourceId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [saveBudget] = useSaveBudgetMutation();
  const [isReady, setIsReady] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Fetch the source budget to pre-fill values
  const { data: sourceBudget, error: sourceError, isLoading } = useGetBudgetQuery(
    sourceId ? { id: sourceId } : skipToken,
  );

  // Load lookup data when source budget is available
  useEffect(() => {
    dispatch(getClients());
    dispatch(getClientBudgetStatuses());
    dispatch(toggleQuickMenu(true));

    return () => {
      dispatch(toggleQuickMenu(false));
    };
  }, []);

  // Load client-specific data once we have the source budget
  useEffect(() => {
    if (!sourceBudget?.client_id) return;

    const loadClientData = async () => {
      try {
        await Promise.all([
          dispatch(
            getPricePlans({
              start_date: { lte: sourceBudget.start_date },
              end_date: { gte: sourceBudget.end_date },
            }),
          ),
          dispatch(getSahBudgets({ client_id: sourceBudget.client_id })),
          dispatch(getClientContributions({ client_id: sourceBudget.client_id })),
          dispatch(getClientServices({ client_id: sourceBudget.client_id })),
        ]);
      } catch (err) {
        console.error('Failed to load copy budget lookups:', err);
        setLookupError('Some lookup data failed to load. Selectors may be incomplete.');
      } finally {
        setIsReady(true);
      }
    };

    loadClientData();
  }, [sourceBudget?.client_id]);

  const initialValues = useMemo(() => {
    if (!sourceBudget) return {} as Partial<Budget>;

    return {
      client_id: sourceBudget.client_id,
      client_sah_budget_id: null,
      price_plan_id: sourceBudget.price_plan_id,
      price_plan_revision_id: sourceBudget.price_plan_revision_id,
      package_fixed_id: sourceBudget.package_fixed_id,
      start_date: '',
      end_date: '',
      total_amount: sourceBudget.client_sah_budget_id ? 0 : sourceBudget.total_amount,
      total_includes_case_management: sourceBudget.client_sah_budget_id
        ? null
        : sourceBudget.total_includes_case_management,
      ignore_sah_filters: sourceBudget.ignore_sah_filters ?? false,
      supplements: sourceBudget.supplements ?? [],
      sections: [],
      total_allocated: 0,
      source_budget_id: sourceId,
    } as unknown as Partial<Budget> & { source_budget_id: string };
  }, [sourceBudget, sourceId]);

  const onSaveCopy = async (values: Budget) => {
    setSaveError(null);

    // Filter out removed items before saving
    const filteredSections = (values.sections ?? []).map((section) => ({
      ...section,
      items: section.items.filter((item) => item.copy_status !== COPY_STATUS.REMOVED),
    }));

    const newBudget = {
      client_id: values.client_id,
      client_sah_budget_id: values.client_sah_budget_id,
      price_plan_revision_id: values.price_plan_revision_id,
      client_package_id: values.package_fixed_id,
      total_includes_case_management: values.total_includes_case_management,
      ignore_sah_filters: values.ignore_sah_filters,
      supplements: values.supplements,
      start_date: values.start_date,
      status_id: values.status_id,
      end_date: values.end_date,
      total_amount: values.total_amount,
      sections: filteredSections,
    };

    try {
      const result = await saveBudget(newBudget).unwrap();
      if (result?.id) {
        navigate('/support-at-home/budgets');
        return true;
      }
      setSaveError('Budget was saved but no ID was returned. Please check the budgets list.');
    } catch (err) {
      console.error('Error saving copied budget:', err);
      setSaveError(getErrorMessage(err));
    }
    return false;
  };

  const onCancel = () => navigate('/support-at-home/budgets');

  const beforeStepChange = async (step: number, values: Budget) => {
    if (step === 4) {
      const editableItems = (values.sections ?? []).flatMap((section) =>
        section.items.filter((item) => item.copy_status !== COPY_STATUS.REMOVED),
      );

      const hasInvalidItem = editableItems.some((item) => {
        const { billing_type, start_date, end_date, shift_id, frequency_id } = item;
        if (!start_date || !end_date) return true;
        if (!billing_type || !isInvoiceBillingType(billing_type)) {
          return !shift_id || !frequency_id;
        }
        return false;
      });

      if (hasInvalidItem) {
        showToast(
          'danger',
          'All chosen service types must have valid start/end dates. Shift & frequency must also be selected if available.',
          5000,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
    }
    return true;
  };

  if (sourceError) {
    return (
      <div className="p-2 budget">
        <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
          Copy Budget
        </ContainerHeader>
        <ErrorResponse message={getErrorMessage(sourceError)} />
      </div>
    );
  }

  if (isLoading || !isReady) {
    return (
      <div className="p-2 budget">
        <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
          Copy Budget
        </ContainerHeader>
        <div className="text-muted p-3">Loading source budget...</div>
      </div>
    );
  }

  return (
    <div className="p-2 budget">
      <ContainerHeader icon={faCopy} iconSize="1x" className="info" dataCy="copy-budget-header">
        Copy Budget
      </ContainerHeader>
      {lookupError && <ErrorResponse message={lookupError} />}
      {saveError && <ErrorResponse message={saveError} />}
      <WizardForm
        steps={copyBudgetFields}
        initialValues={initialValues as unknown as Budget}
        bodyClassname="d-flex flex-column flex-grow-1"
        onComplete={onSaveCopy}
        onCancel={onCancel}
        beforeStepChange={beforeStepChange}
      />
    </div>
  );
};

export default BudgetCopy;
